import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';
import StaffScheduleManager from './StaffScheduleManager';
import StaffAnalytics from './StaffAnalytics';
import { useTenant } from './contexts/TenantContext';

function Staff() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [showScheduleManager, setShowScheduleManager] = useState(false);
  const [selectedStaffForSchedule, setSelectedStaffForSchedule] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [selectedStaffForAnalytics, setSelectedStaffForAnalytics] = useState(null);
  const { subdomain, businessType } = useTenant();
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedStaffForPin, setSelectedStaffForPin] = useState(null);
  const [pinValue, setPinValue] = useState('');
  const isBarbershop = ['barbershop', 'barber'].includes((businessType || '').toLowerCase());
  const copyBookingLink = (member) => {
    const name = `${member.first_name}-${member.last_name || ''}`.toLowerCase().replace(/\s+/g, '-');
    const link = `https://ivisionmarketing.com/book/${subdomain}?staff=${name}`;
    navigator.clipboard.writeText(link);
    alert(`Copied: ${link}`);
  };

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'staff',
    commission_rate: 50,
    hourly_rate: 0,
    is_active: true
  });

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/staff');
      if (response.data.success) {
        setStaff(response.data.staff || []);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading staff:', err);
      setError('Failed to load staff');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingStaff) {
        const response = await axios.put(
          `/api/staff/${editingStaff.id}`,
          formData
        );
        if (response.data.success) {
          alert('✅ Staff member updated successfully!');
          closeModal();
          loadStaff();
        }
      } else {
        const response = await axios.post('/api/staff', formData);
        if (response.data.success) {
          alert('✅ Staff member created successfully!');
          closeModal();
          loadStaff();
        }
      }
    } catch (err) {
      console.error('Error saving staff:', err);
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      first_name: staffMember.first_name || '',
      last_name: staffMember.last_name || '',
      email: staffMember.email || '',
      phone: staffMember.phone || '',
      role: staffMember.role || 'staff',
      commission_rate: staffMember.commission_rate || 50,
      hourly_rate: staffMember.hourly_rate || 0,
      is_active: staffMember.is_active !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) {
      return;
    }

    try {
      const response = await axios.delete(`/api/staff/${staffId}`);
      if (response.data.success) {
        alert('✅ Staff member deleted successfully!');
        loadStaff();
      }
    } catch (err) {
      console.error('Error deleting staff:', err);
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleViewCommissions = (staffMember) => {
    setSelectedStaffForAnalytics(staffMember);
    setShowAnalytics(true);
  };

  const handleManageSchedule = (staffMember) => {
    setSelectedStaffForSchedule(staffMember);
    setShowScheduleManager(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingStaff(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role: 'staff',
      commission_rate: 50,
      hourly_rate: 0,
      is_active: true
    });
  };
  const handleSetPin = async () => {
    if (!pinValue || pinValue.length < 4 || pinValue.length > 6) {
      alert("PIN must be 4-6 digits");
      return;
    }
    try {
      const response = await axios.post("/api/staff-auth/set-pin", {
        staff_id: selectedStaffForPin.id,
        pin: pinValue
      });
      if (response.data.success) {
        alert("✅ PIN set successfully!");
        setShowPinModal(false);
        setSelectedStaffForPin(null);
        setPinValue("");
        loadStaff();
      }
    } catch (err) {
      console.error("Error setting PIN:", err);
      alert(err.response?.data?.error || "Failed to set PIN");
    }
  };
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-2xl text-slate-400">Loading staff...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="bg-surface p-8 rounded-lg border border-red-700 max-w-md">
          <h2 className="text-xl font-bold text-red-500 mb-4">⚠️ Error</h2>
          <p className="text-slate-300 mb-4">{error}</p>
          <button
            onClick={loadStaff}
            className="w-full bg-primary hover:bg-purple-600 text-white py-2 rounded-lg font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">👥 Staff Management</h1>
          <p className="text-slate-400">{staff.length} staff members</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-primary hover:bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:scale-105 transition-all"
        >
          + Add Staff
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {staff.map(member => (
          <div
            key={member.id}
            className="bg-surface p-6 rounded-lg border border-slate-700 hover:border-primary transition-all hover:shadow-lg"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-white font-semibold text-lg">
                    {member.first_name} {member.last_name}
                  </h3>
                  {!member.active && (
                    <span className="bg-slate-700 text-slate-400 text-xs px-2 py-1 rounded">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-slate-400 text-sm">📧 {member.email}</p>
                {member.phone && (
                  <p className="text-slate-500 text-sm">📞 {member.phone}</p>
                )}
                <p className="text-slate-500 text-sm mt-2">
                  💼 {member.role === 'admin' ? 'Admin' : member.role === 'manager' ? 'Manager' : 'Staff'}
                </p>
                {subdomain && (
                  <button
                    onClick={(e) => { e.stopPropagation(); copyBookingLink(member); }}
                    className="mt-2 text-xs px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-blue-400 hover:text-blue-300 transition-all flex items-center gap-1"
                    title="Copy direct booking link for this staff member"
                  >
                    📋 Copy Booking Link
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(member)}
                  className="text-blue-400 hover:text-blue-300 text-sm"
                  title="Edit"
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDelete(member.id)}
                  className="text-red-400 hover:text-red-300 text-sm"
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>

            <div className="space-y-2 mb-4 p-3 bg-background rounded-lg">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Commission Rate:</span>
                <span className="text-emerald-500 font-semibold">{member.commission_rate}%</span>
              </div>
              {member.hourly_rate > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Hourly Rate:</span>
                  <span className="text-white font-semibold">${member.hourly_rate}/hr</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Total Earnings:</span>
                <span className="text-emerald-500 font-bold">
                  ${parseFloat(member.total_earnings || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Completed Services:</span>
                <span className="text-white font-semibold">{member.completed_services || 0}</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handleManageSchedule(member)}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-semibold transition-colors"
              >
                📅 Schedule
              </button>
              <button
                onClick={() => handleViewCommissions(member)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white py-2 rounded-lg text-sm font-semibold transition-colors"
              >
                💰 Analytics
              </button>
              <button
                onClick={() => { setSelectedStaffForPin(member); setPinValue(""); setShowPinModal(true); }}
                className="bg-amber-600 hover:bg-amber-700 text-white py-2 rounded-lg text-sm font-semibold transition-colors"
              >
                🔑 {member.pin ? "Change PIN" : "Set PIN"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {staff.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">👥</div>
          <p className="text-slate-400 text-lg mb-2">No staff members yet</p>
          <button
            onClick={() => setShowModal(true)}
            className="text-primary hover:text-purple-400 font-semibold"
          >
            Add your first staff member →
          </button>
        </div>
      )}

      {/* Add/Edit Staff Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-surface rounded-lg max-w-2xl w-full p-6 my-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                {editingStaff ? '✏️ Edit Staff Member' : '➕ New Staff Member'}
              </h2>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-white text-3xl leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">First Name *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="w-full bg-background border border-slate-700 text-white p-3 rounded-lg focus:border-primary focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Last Name *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="w-full bg-background border border-slate-700 text-white p-3 rounded-lg focus:border-primary focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full bg-background border border-slate-700 text-white p-3 rounded-lg focus:border-primary focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full bg-background border border-slate-700 text-white p-3 rounded-lg focus:border-primary focus:outline-none"
                    placeholder="702-555-1234"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full bg-background border border-slate-700 text-white p-3 rounded-lg focus:border-primary focus:outline-none"
                >
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Commission Rate (%)</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={formData.commission_rate}
                    onChange={(e) => setFormData({...formData, commission_rate: parseFloat(e.target.value)})}
                    className="w-full bg-background border border-slate-700 text-white p-3 rounded-lg focus:border-primary focus:outline-none"
                  />
                  <p className="text-slate-500 text-xs mt-1">Percentage of service price</p>
                </div>
                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Hourly Rate (Optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({...formData, hourly_rate: parseFloat(e.target.value) || 0})}
                    className="w-full bg-background border border-slate-700 text-white p-3 rounded-lg focus:border-primary focus:outline-none"
                    placeholder="0.00"
                  />
                  <p className="text-slate-500 text-xs mt-1">$ per hour</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-5 h-5 bg-background border border-slate-700 rounded focus:ring-primary"
                />
                <label htmlFor="is_active" className="text-slate-300 font-semibold">
                  Active (Can take bookings)
                </label>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-primary hover:bg-purple-600 text-white py-3 rounded-lg font-semibold shadow-lg hover:scale-105 transition-all"
                >
                  {editingStaff ? '✅ Update Staff' : '✅ Create Staff'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Schedule Manager Modal */}
      {showScheduleManager && selectedStaffForSchedule && (
        <StaffScheduleManager
          staffId={selectedStaffForSchedule.id}
          staffName={`${selectedStaffForSchedule.first_name} ${selectedStaffForSchedule.last_name}`}
          onClose={() => {
            setShowScheduleManager(false);
            setSelectedStaffForSchedule(null);
          }}
        />
      )}

      {/* Analytics Modal */}
      {showAnalytics && selectedStaffForAnalytics && (
        <StaffAnalytics
          staffId={selectedStaffForAnalytics.id}
          staffName={`${selectedStaffForAnalytics.first_name} ${selectedStaffForAnalytics.last_name}`}
          onClose={() => {
            setShowAnalytics(false);
            setSelectedStaffForAnalytics(null);
          }}
        />
      )}
      {/* Set PIN Modal */}
      {showPinModal && selectedStaffForPin && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-lg max-w-sm w-full p-6">
            <h2 className="text-xl font-bold text-white mb-4">
              🔑 Set PIN for {selectedStaffForPin.first_name} {selectedStaffForPin.last_name}
            </h2>
            <p className="text-slate-400 text-sm mb-4">Enter a 4-6 digit PIN for employee portal login.</p>
            <input
              type="text"
              maxLength={6}
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value.replace(/[^0-9]/g, ""))}
              className="w-full bg-background border border-slate-700 text-white p-4 rounded-lg text-center text-2xl tracking-widest focus:border-primary focus:outline-none mb-4"
              placeholder="• • • •"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={handleSetPin}
                className="flex-1 bg-primary hover:bg-purple-600 text-white py-3 rounded-lg font-semibold"
              >
                ✅ Save PIN
              </button>
              <button
                onClick={() => { setShowPinModal(false); setSelectedStaffForPin(null); setPinValue(""); }}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}

export default Staff;

